import {
  CubeReducerState,
  DisabledReason,
  DisabledReasonType,
  VisibleEnabledFeatureAvailable
} from 'modules/Cube/domain/cube.domain.types'

/**
 * Whether a different amendment of the same schedule was accepted ahead of this quote. The backend
 * refuses to publish or execute it from then on, so there is nothing left to do with it but
 * archive it.
 */
export const isQuoteSuperseded = (
  prevState: Pick<CubeReducerState, 'data'>
): boolean => Boolean(prevState.data.quote.amendment?.supersededByQuoteId)

export const supersededQuoteReason = (
  prevState: Pick<CubeReducerState, 'data'>
): DisabledReason => ({
  reasonType: DisabledReasonType.QuoteSuperseded,
  metadata: {
    supersededByQuoteId: prevState.data.quote.amendment?.supersededByQuoteId
  }
})

/**
 * Takes a feature's own answer and switches it off while the quote is superseded, keeping whatever
 * that feature decided about `visible`. For the controls that stay on screen, so the sidebar can
 * say why they no longer respond.
 */
export const disableIfSuperseded =
  (prevState: Pick<CubeReducerState, 'data'>) =>
  (feature: VisibleEnabledFeatureAvailable): VisibleEnabledFeatureAvailable => {
    if (!isQuoteSuperseded(prevState)) {
      return feature
    }

    return {
      available: {
        visible: feature.available.visible,
        enabled: false
      },
      reasons: [supersededQuoteReason(prevState), ...feature.reasons]
    }
  }

/**
 * Takes a feature off the screen entirely. For the actions that would move the quote on, which a
 * superseded one has no version of - archiving is meant to be the only thing left, so these go
 * rather than sit there greyed.
 */
export const hideIfSuperseded =
  (prevState: Pick<CubeReducerState, 'data'>) =>
  (feature: VisibleEnabledFeatureAvailable): VisibleEnabledFeatureAvailable => {
    if (!isQuoteSuperseded(prevState)) {
      return feature
    }

    return {
      available: {
        visible: false,
        enabled: false
      },
      reasons: [supersededQuoteReason(prevState), ...feature.reasons]
    }
  }
